import React, { useCallback, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { packagingApi } from 'api/packaging';
import { ViewEntityHeader } from 'components/entityViewer/ViewEntityHeader';
import { ViewEntityPage } from 'components/entityViewer/ViewEntityPage';
import { PackagingPurchaseOrderHeaderContent } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderHeaderContent';
import { PackagingPurchaseOrderItemsCard } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderItemsCard';
import { PackagingPurchaseOrderPrimaryInfoCard } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderPrimaryInfoCard';
import { usePackagingPurchaseOrderItems } from 'components/packaging/components/packagingPOV2/entityView/PackagingPurchaseOrderQueries';
import { toMoment } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import { Button } from 'shadcn/components/ui/Button';

const buildPackagingPrimaryDraft = (purchaseOrder) => {
    const scheduledDelivery = toMoment(purchaseOrder?.scheduledDeliveryDate);
    return {
        scheduledDeliveryDate: scheduledDelivery ? scheduledDelivery.format('YYYY-MM-DD') : '',
        scheduledDeliveryTime: scheduledDelivery ? scheduledDelivery.format('HH:mm') : '',
        referenceNumber: purchaseOrder?.referenceNumber ?? '',
        vendorId: purchaseOrder?.vendorID ?? purchaseOrder?.vendorId ?? null,
    };
};

const validatePackagingPrimaryDraft = (draft) => {
    const errors = [];
    const fieldErrors = {};

    if (!draft?.vendorId) {
        fieldErrors.vendorId = 'Vendor is required.';
        errors.push('Vendor is required.');
    }

    if (!draft?.scheduledDeliveryDate) {
        fieldErrors.scheduledDeliveryDate = 'Scheduled delivery date is required.';
        errors.push('Scheduled delivery date is required.');
    }

    if (!draft?.scheduledDeliveryTime) {
        fieldErrors.scheduledDeliveryTime = 'Scheduled delivery time is required.';
        errors.push('Scheduled delivery time is required.');
    }

    return { errors, fieldErrors };
};

export function ViewPackagingPOEntity() {
    const { purchaseOrderId } = useParams();
    const location = useLocation();
    const history = useHistory();

    const {
        data: purchaseOrderDetail,
        error,
        isError,
        isFetching,
        isLoading,
        refetch,
    } = usePackagingPurchaseOrderItems({
        id: purchaseOrderId,
        enabled: Boolean(purchaseOrderId),
    });

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [primaryDraft, setPrimaryDraft] = useState(null);
    const [primaryErrors, setPrimaryErrors] = useState([]);
    const [primaryFieldErrors, setPrimaryFieldErrors] = useState({});
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

    const purchaseOrderFromState = location.state?.purchaseOrder ?? null;
    const resolvedPurchaseOrderDetail = useMemo(
        () => purchaseOrderDetail?.output ?? purchaseOrderDetail ?? null,
        [purchaseOrderDetail],
    );
    const headerPurchaseOrder = useMemo(
        () => purchaseOrderFromState ?? resolvedPurchaseOrderDetail ?? null,
        [purchaseOrderFromState, resolvedPurchaseOrderDetail],
    );
    const purchaseOrderItems = useMemo(() => {
        if (Array.isArray(resolvedPurchaseOrderDetail?.items)) {
            return resolvedPurchaseOrderDetail.items;
        }
        return [];
    }, [resolvedPurchaseOrderDetail]);
    const { totalCases, totalUnits } = useMemo(() => {
        let casesTotal = 0;
        let unitsTotal = 0;
        purchaseOrderItems.forEach((item) => {
            const qty = Number(item?.qty);
            if (Number.isFinite(qty)) {
                casesTotal += qty;
            }
            const unitsPerCase = Number(item?.unitsPerCase);
            if (Number.isFinite(qty) && Number.isFinite(unitsPerCase)) {
                unitsTotal += qty * unitsPerCase;
            }
        });
        return { totalCases: casesTotal, totalUnits: unitsTotal };
    }, [purchaseOrderItems]);

    const handleEditPurchaseOrder = useCallback(() => {
        if (!headerPurchaseOrder) return;
        setIsEditing(true);
        setHasAttemptedSave(false);
        setPrimaryErrors([]);
        setPrimaryFieldErrors({});
        setPrimaryDraft(buildPackagingPrimaryDraft(headerPurchaseOrder));
    }, [headerPurchaseOrder]);

    const handleCancelEditPurchaseOrder = useCallback(() => {
        setIsEditing(false);
        setPrimaryDraft(null);
        setPrimaryErrors([]);
        setPrimaryFieldErrors({});
        setHasAttemptedSave(false);
    }, []);

    const handleSavePurchaseOrder = useCallback(async () => {
        if (!headerPurchaseOrder) return;

        const { errors, fieldErrors } = validatePackagingPrimaryDraft(primaryDraft);
        setPrimaryErrors(errors);
        setPrimaryFieldErrors(fieldErrors);
        setHasAttemptedSave(true);

        if (errors.length > 0) return;

        setIsSaving(true);
        try {
            const response = await packagingApi.updatePurchaseOrder(headerPurchaseOrder.id ?? purchaseOrderId, {
                scheduledDeliveryDate: primaryDraft.scheduledDeliveryDate,
                deliverTime: primaryDraft.scheduledDeliveryTime,
                referenceNumber: primaryDraft.referenceNumber,
                vendorID: primaryDraft.vendorId,
            });

            if (!response?.status) {
                const message = response?.msg
                    ? `Failed to save purchase order. ${response.msg}`
                    : 'Failed to save purchase order.';
                toast.error(message);
                return;
            }

            toast.success('Purchase order updated.');
            setIsEditing(false);
            setPrimaryDraft(null);
            setPrimaryErrors([]);
            setPrimaryFieldErrors({});
            setHasAttemptedSave(false);
            await refetch();
        } catch (err) {
            toast.error(`Failed to save purchase order. ${err?.message ?? ''}`.trim());
        } finally {
            setIsSaving(false);
        }
    }, [headerPurchaseOrder, primaryDraft, purchaseOrderId, refetch]);

    const purchaseOrdersFiltersFromState =
        typeof location.state?.purchaseOrdersFilters === 'string' && location.state.purchaseOrdersFilters.length > 0
            ? location.state.purchaseOrdersFilters
            : '';
    const purchaseOrdersFilters =
        purchaseOrdersFiltersFromState ||
        (typeof location?.search === 'string' && location.search ? location.search : '') ||
        (typeof window !== 'undefined' && window?.location?.search ? window.location.search : '') ||
        '';
    const backHref = purchaseOrdersFilters
        ? { pathname: '/packaging/viewPurchaseOrder-v2', search: purchaseOrdersFilters }
        : '/packaging/viewPurchaseOrder-v2';

    if (isLoading || isFetching) {
        return (
            <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Loading purchase order…</span>
                </div>
            </div>
        );
    }

    if (isError) {
        const errorMessage = error?.message || 'Failed to load purchase order';
        return (
            <div className="flex min-h-[400px] items-center justify-center rounded-md border border-border bg-card px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-4 max-w-md">
                    <AlertCircle className="h-8 w-8 text-destructive" />
                    <div className="flex flex-col items-center gap-2">
                        <span className="text-sm font-medium text-foreground">Unable to load purchase order</span>
                        <span className="text-sm text-muted-foreground">{errorMessage}</span>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
                        <Button variant="outline" onClick={() => refetch()}>
                            Retry
                        </Button>
                        <Button variant="outline" onClick={() => history.push(backHref)}>
                            Back to POs
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const header = (
        <ViewEntityHeader
            backHref={backHref}
            backLabel="Back to POs"
            entityIdentificationComponent={
                <PackagingPurchaseOrderHeaderContent
                    purchaseOrder={headerPurchaseOrder}
                    purchaseOrderId={purchaseOrderId}
                    onEdit={isEditing ? undefined : handleEditPurchaseOrder}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    onCancel={handleCancelEditPurchaseOrder}
                    onSave={handleSavePurchaseOrder}
                    hasValidationErrors={hasAttemptedSave && Object.keys(primaryFieldErrors).length > 0}
                />
            }
        />
    );

    const primaryInformationCard = (
        <PackagingPurchaseOrderPrimaryInfoCard
            purchaseOrder={headerPurchaseOrder}
            isEditing={isEditing}
            draft={primaryDraft}
            onDraftChange={setPrimaryDraft}
            primaryErrors={primaryErrors}
            primaryFieldErrors={primaryFieldErrors}
            showValidation={hasAttemptedSave}
            totalCases={totalCases}
            totalUnits={totalUnits}
        />
    );
    const itemInformationCard = <PackagingPurchaseOrderItemsCard isEditing={isEditing} />;

    return (
        <ViewEntityPage
            header={header}
            primaryInformationCard={primaryInformationCard}
            additionalInformationCards={itemInformationCard}
        />
    );
}

ViewPackagingPOEntity.displayName = 'ViewPackagingPOEntity';
